import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Image, Video, Mail, Search, FileText, Trash2, Lock, Crown, Sparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TemplateThumbnail } from '../components/TemplateThumbnail';
import { NewsletterThumbnail } from '../components/NewsletterThumbnail';
import { AppNavbar } from '../components/AppNavbar';
import { MobileTabBar } from '../components/MobileTabBar';

export const TemplatesPage = () => {
  const { user } = useAuth();
  const { plan, promptUpgrade } = usePlan();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  // Pro/Team/Admin/Owner unlock premium. Free + Starter see a lock.
  const hasPremiumAccess = isAdmin || plan === 'pro' || plan === 'team';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'all';
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(initialType);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDeleteTemplate = async (e, template) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/templates/${template.template_id}`);
      setTemplates(prev => prev.filter(t => t.template_id !== template.template_id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleTogglePremium = async (e, template) => {
    e.stopPropagation();
    const next = !template.is_premium;
    try {
      await apiClient.patch(`/templates/${template.template_id}/premium`, { is_premium: next });
      setTemplates(prev => prev.map(t => t.template_id === template.template_id ? { ...t, is_premium: next } : t));
      toast.success(next ? 'Marked as Premium' : 'Removed Premium flag');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleTemplateClick = (template) => {
    if (template.is_premium && !hasPremiumAccess) {
      promptUpgrade(
        `"${template.name}" is a Premium template. Upgrade to Pro to unlock our full premium library.`,
        'pro'
      );
      return;
    }
    navigate(`/create-campaign/${template.template_id}`);
  };

  // Sync filter when URL changes (e.g., user clicks another sidebar link)
  useEffect(() => {
    const t = searchParams.get('type') || 'all';
    setSelectedType(t);
  }, [searchParams]);

  useEffect(() => {
    filterTemplates();
  }, [searchQuery, selectedType, templates]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/templates');
      setTemplates(response.data.templates);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(t => t.type === selectedType);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(t => 
        (t.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.category || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Pin premium templates to the top of the grid to raise upgrade intent.
    // Stable sort: premium first, otherwise preserve original order.
    filtered = [...filtered].sort((a, b) => {
      const aP = a.is_premium ? 1 : 0;
      const bP = b.is_premium ? 1 : 0;
      return bP - aP;
    });
    
    setFilteredTemplates(filtered);
  };

  // Total premium templates in the library (used by empty-state CTA for free users)
  const premiumCount = templates.filter(t => t.is_premium).length;


  const getTypeIcon = (type) => {
    switch(type) {
      case 'post': return <Image className="h-5 w-5" />;
      case 'reel': return <Video className="h-5 w-5" />;
      case 'newsletter': return <Mail className="h-5 w-5" />;
      case 'flyer': return <FileText className="h-5 w-5" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <div className="noise-texture" />
      
      <AppNavbar />

      {/* Templates Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="templates-title">Template Library</h2>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-templates-input"
            />
          </div>

          <Tabs value={selectedType} onValueChange={setSelectedType}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="post" data-testid="filter-posts">Posts</TabsTrigger>
              <TabsTrigger value="story" data-testid="filter-stories">Stories</TabsTrigger>
              <TabsTrigger value="reel" data-testid="filter-reels">Reels</TabsTrigger>
              <TabsTrigger value="carousel" data-testid="filter-carousels">Carousels</TabsTrigger>
              <TabsTrigger value="newsletter" data-testid="filter-newsletters">Newsletters</TabsTrigger>
              <TabsTrigger value="flyer" data-testid="filter-flyers">Flyers</TabsTrigger>
              <TabsTrigger value="postcard" data-testid="filter-postcards">Postcards</TabsTrigger>
              <TabsTrigger value="brochure" data-testid="filter-brochures">Brochures</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p>Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-20" data-testid="templates-empty-state">
            <p className="text-muted-foreground mb-6">No templates found.</p>
            {/* Free/starter users with zero matches still have premium options to peek at.
                Surfacing this CTA on the empty state recovers users who would otherwise bounce. */}
            {!hasPremiumAccess && premiumCount > 0 && (
              <button
                onClick={() => promptUpgrade(
                  `Unlock ${premiumCount} Premium templates — exclusive designs handpicked by our team.`,
                  'pro'
                )}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="empty-state-browse-premium-cta"
              >
                <Crown className="h-4 w-4" />
                Browse all {premiumCount} Pro templates
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="templates-grid">
            {filteredTemplates.map((template) => {
              const isLocked = template.is_premium && !hasPremiumAccess;
              return (
              <div
                key={template.template_id}
                className={`relative border border-border/50 bg-card rounded-lg overflow-hidden card-hover group ${isLocked ? 'cursor-pointer' : 'cursor-pointer'}`}
                onClick={() => handleTemplateClick(template)}
                data-testid={`template-card-${template.template_id}`}
              >
                {/* Premium badge — visible to ALL when template is premium */}
                {template.is_premium && (
                  <div
                    className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-[10px] font-semibold uppercase tracking-wider shadow-sm"
                    data-testid={`template-premium-badge-${template.template_id}`}
                  >
                    <Crown className="h-3 w-3" />
                    <span>Pro</span>
                  </div>
                )}

                {isAdmin && (
                  <div className="absolute top-2 right-2 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleTogglePremium(e, template)}
                      className={`h-8 px-2.5 flex items-center gap-1 rounded-full backdrop-blur border shadow-sm active:scale-95 transition-all text-[11px] font-medium ${template.is_premium ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100' : 'bg-white/95 border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      title={template.is_premium ? 'Remove Premium flag' : 'Mark as Premium (Pro/Team only)'}
                      aria-label="Toggle premium"
                      data-testid={`template-premium-toggle-${template.template_id}`}
                    >
                      <Sparkles className="h-3 w-3" />
                      {template.is_premium ? 'Premium' : 'Mark Pro'}
                    </button>
                    <button
                      onClick={(e) => handleDeleteTemplate(e, template)}
                      className="h-8 w-8 flex items-center justify-center rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-sm hover:bg-red-50 hover:border-red-300 active:scale-95 transition-all"
                      title="Delete template"
                      aria-label="Delete template"
                      data-testid={`template-delete-${template.template_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  </div>
                )}
                <div className="relative bg-secondary/10 flex items-center justify-center overflow-hidden">
                  {template.type === 'newsletter' && template.canvas_data?.blocks ? (
                    <NewsletterThumbnail canvasData={template.canvas_data} width={280} />
                  ) : template.canvas_data ? (
                    <TemplateThumbnail canvasData={template.canvas_data} width={280} />
                  ) : template.thumbnail_url ? (
                    <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="aspect-video flex items-center justify-center w-full">
                      {getTypeIcon(template.type)}
                    </div>
                  )}

                  {/* Lock overlay — only when template is premium AND user lacks access */}
                  {isLocked && (
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 backdrop-blur-[2px] opacity-100 group-hover:bg-black/65 transition-all"
                      data-testid={`template-lock-overlay-${template.template_id}`}
                    >
                      <div className="h-12 w-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center mb-2 border border-white/25">
                        <Lock className="h-5 w-5 text-white" />
                      </div>
                      <p className="text-white text-xs font-semibold uppercase tracking-widest mb-1">Premium</p>
                      <p className="text-white/85 text-[11px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Upgrade to Pro to unlock
                      </p>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {getTypeIcon(template.type)}
                    <span className="text-xs text-muted-foreground capitalize">{template.type}</span>
                  </div>
                  <h3 className="font-semibold mb-1">{template.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{(template.category || 'general').replace('_', ' ')}</p>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>
      <MobileTabBar />
    </div>
  );
};